import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Button from './Button';
import { STATUS_GROUPS, STATUS_LABELS } from '../config/patientStatus';

export default function StatusTransitionModal({ patient, onClose, onSaved }) {
  const today = new Date().toISOString().split('T')[0];

  const [targetStatus, setTargetStatus] = useState('');
  const [transitionDate, setTransitionDate] = useState(today);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [reasons, setReasons] = useState([]);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!targetStatus) { setReasons([]); setReason(''); return; }
    api.get(`/reference/discharge-reasons?transition_type=${targetStatus}`)
      .then(res => { setReasons(res.data); setReason(''); })
      .catch(() => setReasons([]));
  }, [targetStatus]);

  const validate = () => {
    const errs = {};
    if (!targetStatus) errs.targetStatus = 'Please select a status';
    if (!transitionDate) errs.transitionDate = 'Required';
    if (targetStatus === patient.current_status) {
      errs.targetStatus = `Patient is already ${STATUS_LABELS[targetStatus] || targetStatus}`;
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await api.post(`/patients/${patient.id}/status`, {
        status: targetStatus,
        transition_date: transitionDate,
        reason: reason || null,
        notes: notes || null,
      });
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      setErrors({ _general: typeof detail === 'string' ? detail : 'Failed to update status.' });
    } finally {
      setSaving(false);
    }
  };

  const filteredGroups = STATUS_GROUPS.map(group => ({
    ...group,
    options: group.options.filter(o => o.value !== patient.current_status),
  })).filter(group => group.options.length > 0);

  return (
    <div style={styles.overlay}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h2 style={styles.title}>Change Patient Status</h2>
            <p style={styles.subtitle}>
              {patient.last_name}, {patient.first_name} &nbsp;·&nbsp; MRN: {patient.mrn}
            </p>
          </div>
          {/* Close is an icon dismiss control — plain button intentional */}
          <button onClick={onClose} style={styles.closeBtn}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Current status */}
        <div style={styles.currentStatus}>
          <span style={styles.currentLabel}>Current status:</span>
          <span style={styles.currentValue}>
            {STATUS_LABELS[patient.current_status] || patient.current_status}
          </span>
        </div>

        {/* Body */}
        <div style={styles.body}>
          {errors._general && (
            <div style={styles.generalError}>{errors._general}</div>
          )}

          <div style={styles.fieldGroup}>
            <label style={styles.label}>New Status *</label>
            <select
              style={fieldStyle(errors.targetStatus)}
              value={targetStatus}
              onChange={e => { setTargetStatus(e.target.value); setErrors({}); }}
            >
              <option value="">— Select new status —</option>
              {filteredGroups.map(group => (
                <optgroup key={group.label} label={group.label}>
                  {group.options.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </optgroup>
              ))}
            </select>
            {errors.targetStatus && <span style={styles.fieldError}>{errors.targetStatus}</span>}
          </div>

          <div style={styles.fieldGroup}>
            <label style={styles.label}>Transition Date *</label>
            <input
              type="date"
              style={fieldStyle(errors.transitionDate)}
              value={transitionDate}
              onChange={e => setTransitionDate(e.target.value)}
            />
            {errors.transitionDate && <span style={styles.fieldError}>{errors.transitionDate}</span>}
          </div>

          {reasons.length > 0 && (
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Reason</label>
              <select
                style={styles.input}
                value={reason}
                onChange={e => setReason(e.target.value)}
              >
                <option value="">— Select reason (optional) —</option>
                {reasons.map(r => (
                  <option key={r.id} value={r.label}>{r.label}</option>
                ))}
              </select>
            </div>
          )}

          <div style={styles.fieldGroup}>
            <label style={styles.label}>
              Notes <span style={styles.optional}>(optional)</span>
            </label>
            <textarea
              style={styles.textarea}
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Any additional notes about this transition..."
              rows={3}
            />
          </div>

          {targetStatus && targetStatus !== 'active' && (
            <div style={styles.warningBox}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                style={{ flexShrink: 0, marginTop: '1px' }}>
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="8" x2="12" y2="12"/>
                <line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span>
                {targetStatus.startsWith('esrd_')
                  ? 'Transitioning to ESRD will discontinue active ESA and IV iron orders. Anemia management passes to the dialysis or transplant team.'
                  : targetStatus === 'deceased'
                  ? 'Marking a patient as deceased will discontinue all active orders.'
                  : 'Transitioning away from active care will discontinue active ESA and IV iron orders.'
                }
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !targetStatus}>
            {saving ? 'Saving...' : 'Confirm Status Change'}
          </Button>
        </div>
      </div>
    </div>
  );
}

const fieldStyle = (hasError) => ({
  ...styles.input,
  borderColor: hasError ? '#dc2626' : 'var(--color-border)',
});

const styles = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 },
  modal: { backgroundColor: 'var(--color-bg-card)', borderRadius: 'var(--radius-lg)', width: '520px', maxWidth: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: 'var(--shadow-lg)' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '20px 24px 16px', borderBottom: '1px solid var(--color-border)' },
  title: { fontSize: '17px', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)', margin: '0 0 4px', fontFamily: 'var(--font-family-base)' },
  subtitle: { fontSize: '13px', color: 'var(--color-text-muted)', margin: 0, fontFamily: 'var(--font-family-base)' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', padding: '4px', display: 'flex', flexShrink: 0 },
  currentStatus: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 24px', backgroundColor: 'var(--color-bg-subtle)', borderBottom: '1px solid var(--color-border)' },
  currentLabel: { fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontFamily: 'var(--font-family-base)' },
  currentValue: { fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-primary)', fontFamily: 'var(--font-family-base)' },
  body: { flex: 1, overflowY: 'auto', padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: '16px' },
  generalError: { padding: '10px 14px', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 'var(--radius-sm)', fontSize: '13px', color: '#991b1b', fontFamily: 'var(--font-family-base)' },
  fieldGroup: { display: 'flex', flexDirection: 'column', gap: '5px' },
  label: { fontSize: 'var(--font-size-xs)', fontWeight: 'var(--font-weight-medium)', color: 'var(--color-text-secondary)', fontFamily: 'var(--font-family-base)' },
  optional: { fontWeight: 'var(--font-weight-regular)', color: 'var(--color-text-muted)' },
  input: { width: '100%', padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)', outline: 'none', boxSizing: 'border-box', backgroundColor: 'var(--color-bg-card)', fontFamily: 'var(--font-family-base)' },
  textarea: { width: '100%', padding: '8px 10px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)', outline: 'none', boxSizing: 'border-box', resize: 'vertical', fontFamily: 'var(--font-family-base)' },
  fieldError: { fontSize: '11px', color: '#dc2626', fontFamily: 'var(--font-family-base)' },
  warningBox: { display: 'flex', gap: '8px', alignItems: 'flex-start', padding: '10px 14px', backgroundColor: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 'var(--radius-sm)', fontSize: 'var(--font-size-xs)', color: '#92400e', lineHeight: '1.5', fontFamily: 'var(--font-family-base)' },
  footer: { display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '16px 24px', borderTop: '1px solid var(--color-border)' },
};